import React, { useState } from 'react';
import { PageShell } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';

const DEFAULT_FLEET = [
  { regNo: 'MH01AB1001', type: 'ALS (Advanced Life Support)', model: 'TATA Winger', year: 2022, driver: 'Suresh Kumar', driverPhone: '9876543210', paramedic: 'EMT Ravi', equipment: 'Defibrillator, Ventilator, Suction, Monitor', status: 'Available', location: 'Hospital Bay 1', lastService: '2024-01-01', fuelLevel: 85 },
  { regNo: 'MH01AB1002', type: 'BLS (Basic Life Support)', model: 'Force Traveller', year: 2021, driver: 'Kumar Singh', driverPhone: '9876543211', paramedic: 'EMT Priya', equipment: 'Oxygen, BP Apparatus, Stretcher', status: 'Available', location: 'Hospital Bay 1', lastService: '2024-01-05', fuelLevel: 62 },
];

const statusColor = { Available: '#10b981', 'On Call': '#f59e0b', 'Under Maintenance': '#dc2626', 'Out of Service': '#94a3b8' };
const tripStatusColor = { 'En Route': '#f59e0b', Completed: '#10b981', Dispatched: '#3b82f6', Cancelled: '#dc2626' };
const priorityColor = { P1: '#dc2626', P2: '#f97316', P3: '#f59e0b', P4: '#10b981' };

function normalizeAmb(a) {
  return { ...a, id: a.ambulanceId || a.id };
}

function normalizeTrip(t) {
  return { ...t, id: t.tripId || t.id, amb: t.amb || t.ambulanceId };
}

function FleetTab({ ambulances, loading, onSeedFleet, seeding }) {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Fleet', val: ambulances.length, color: '#6366f1' }, { label: 'Available', val: ambulances.filter(a => a.status === 'Available').length, color: '#10b981' }, { label: 'On Call', val: ambulances.filter(a => a.status === 'On Call').length, color: '#f59e0b' }, { label: 'In Maintenance', val: ambulances.filter(a => a.status === 'Under Maintenance').length, color: '#dc2626' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {ambulances.length === 0 && !loading && (
        <div className="card mb" style={{ padding: 16, textAlign: 'center' }}>
          <p className="hint">No ambulances in fleet yet.</p>
          <button type="button" className="btn btn-primary" disabled={seeding} onClick={onSeedFleet}>{seeding ? 'Adding…' : 'Add sample fleet (2 vehicles)'}</button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 12 }}>
        {ambulances.map(amb => (
          <div key={amb.id} className="card" style={{ borderLeft: `4px solid ${statusColor[amb.status] || '#94a3b8'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: 16, color: '#1e293b' }}>{amb.regNo}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6366f1', marginTop: 2 }}>{amb.type}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: (statusColor[amb.status] || '#94a3b8') + '18', color: statusColor[amb.status] || '#94a3b8', border: `1px solid ${(statusColor[amb.status] || '#94a3b8')}40`, height: 'fit-content' }}>{amb.status}</span>
            </div>
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>{amb.model} ({amb.year}) · Last Service: {amb.lastService}</div>
            <div style={{ fontSize: 12, marginBottom: 4 }}><strong>{amb.driver}</strong> ({amb.driverPhone})</div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Paramedic: <strong>{amb.paramedic}</strong></div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>{amb.location}</div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Fuel</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: amb.fuelLevel < 30 ? '#dc2626' : '#10b981' }}>{amb.fuelLevel}%</span>
              </div>
              <div style={{ background: '#f1f5f9', borderRadius: 4, height: 8 }}>
                <div style={{ width: `${amb.fuelLevel}%`, background: amb.fuelLevel < 30 ? '#dc2626' : '#10b981', height: 8, borderRadius: 4 }} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Equipment: {amb.equipment}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DispatchTab({ trips, ambulances, loading, onDispatch, onComplete }) {
  const available = ambulances.filter(a => a.status === 'Available');
  const [form, setForm] = useState({
    patient: '', pickup: '', destination: 'Hospital ER', priority: 'P1', notes: '',
    amb: '', callTime: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
  });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  async function dispatch() {
    if (!form.pickup.trim()) { alert('Pickup location is required'); return; }
    if (!form.amb) { alert('Select an available ambulance'); return; }
    setSaving(true);
    try {
      await onDispatch(form);
      setShowForm(false);
      setForm({ patient: '', pickup: '', destination: 'Hospital ER', priority: 'P1', notes: '', amb: '', callTime: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) });
    } catch (err) {
      alert(err.message || 'Dispatch failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ padding: '12px 16px', background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: 12, marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 900, color: '#dc2626', fontSize: 15 }}>EMERGENCY DISPATCH CENTER</div>
          <div style={{ fontSize: 13, color: '#475569' }}>Available: {available.length} · ALS Ready: {available.filter(a => (a.type || '').includes('ALS')).length}</div>
        </div>
        <button type="button" className="btn btn-primary" style={{ marginLeft: 'auto', background: '#dc2626', borderColor: '#dc2626' }} disabled={available.length === 0} onClick={() => { setForm(f => ({ ...f, amb: available[0]?.id || '' })); setShowForm(s => !s); }}>New Emergency Dispatch</button>
      </div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #dc2626' }}>
          <h3 className="full" style={{ marginTop: 0, color: '#dc2626' }}>Emergency Dispatch</h3>
          <div className="field"><label>Patient / Caller Name</label><input className="input" value={form.patient} onChange={e => setForm(f => ({ ...f, patient: e.target.value }))} /></div>
          <div className="field"><label>Pickup Location *</label><input className="input" required value={form.pickup} onChange={e => setForm(f => ({ ...f, pickup: e.target.value }))} placeholder="Address / Landmark" /></div>
          <div className="field"><label>Destination</label><input className="input" value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))} /></div>
          <div className="field"><label>Priority</label>
            <select className="input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
              <option value="P1">P1 — Life Threatening</option>
              <option value="P2">P2 — Serious</option>
              <option value="P3">P3 — Urgent</option>
              <option value="P4">P4 — Routine Transfer</option>
            </select>
          </div>
          <div className="field"><label>Assign Ambulance</label>
            <select className="input" value={form.amb} onChange={e => setForm(f => ({ ...f, amb: e.target.value }))}>
              {available.map(a => <option key={a.id} value={a.id}>{a.regNo} — {a.type}</option>)}
            </select>
          </div>
          <div className="field"><label>Call Time</label><input type="time" className="input" value={form.callTime} onChange={e => setForm(f => ({ ...f, callTime: e.target.value }))} /></div>
          <div className="field full"><label>Clinical Information</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Chief complaint, vitals if known…" /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" style={{ background: '#dc2626', borderColor: '#dc2626' }} disabled={saving} onClick={dispatch}>{saving ? 'Dispatching…' : 'Dispatch Ambulance Now'}</button></div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Active & Recent Trips</h3>
        {loading && trips.length === 0 ? <p className="hint">Loading trips…</p> : (
        <div className="table-wrap">
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead><tr><th>Trip ID</th><th>Patient</th><th>Pickup</th><th>Ambulance</th><th>Driver/Paramedic</th><th>Call Time</th><th>Priority</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {trips.map(t => (
                <tr key={t.id}>
                  <td style={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 700 }}>{t.id}</td>
                  <td style={{ fontWeight: 700 }}>{t.patient}<div style={{ fontSize: 10, color: '#94a3b8' }}>{t.notes?.slice(0, 40)}</div></td>
                  <td style={{ fontSize: 11, color: '#64748b' }}>{t.pickup}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{t.regNo || t.amb}</td>
                  <td style={{ fontSize: 11 }}>{t.driver}<br /><span style={{ color: '#94a3b8' }}>{t.paramedic}</span></td>
                  <td>{t.callTime}</td>
                  <td><span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: (priorityColor[t.priority] || '#94a3b8') + '18', color: priorityColor[t.priority] || '#94a3b8', border: `1px solid ${(priorityColor[t.priority] || '#94a3b8')}40` }}>{t.priority}</span></td>
                  <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: (tripStatusColor[t.status] || '#94a3b8') + '18', color: tripStatusColor[t.status] || '#94a3b8', border: `1px solid ${(tripStatusColor[t.status] || '#94a3b8')}40` }}>{t.status}</span></td>
                  <td>{t.status !== 'Completed' && <button type="button" className="btn btn-sm btn-primary" style={{ fontSize: 10 }} onClick={() => onComplete(t.id)}>Complete</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        {!loading && trips.length === 0 && <p className="hint">No trips logged yet.</p>}
      </div>
    </div>
  );
}

export default function AmbulancePage() {
  const [tab, setTab] = useState('dispatch');
  const [seeding, setSeeding] = useState(false);
  const fleetHook = useLiveData(() => ipApi.getAmbulanceFleet(), []);
  const tripsHook = useLiveData(() => ipApi.getAmbulanceTrips(80), []);

  const ambulances = (fleetHook.data?.ambulances || []).map(normalizeAmb);
  const trips = (tripsHook.data?.trips || []).map(normalizeTrip);

  async function onSeedFleet() {
    setSeeding(true);
    try {
      for (const a of DEFAULT_FLEET) {
        await ipApi.createAmbulance(a);
      }
      await fleetHook.reload();
    } catch (err) {
      alert(err.message || 'Failed to add fleet');
    } finally {
      setSeeding(false);
    }
  }

  async function onDispatch(form) {
    const amb = ambulances.find(a => a.id === form.amb);
    await ipApi.createAmbulanceTrip({
      ...form,
      ambulanceId: form.amb,
      driver: amb?.driver,
      paramedic: amb?.paramedic,
      dispatchTime: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
      status: 'Dispatched',
    });
    await Promise.all([tripsHook.reload(), fleetHook.reload()]);
  }

  async function onComplete(tripId) {
    try {
      await ipApi.patchAmbulanceTrip(tripId, { status: 'Completed' });
      await Promise.all([tripsHook.reload(), fleetHook.reload()]);
    } catch (err) {
      alert(err.message || 'Failed to complete trip');
    }
  }

  return (
    <PageShell title="Ambulance & Emergency Services" subtitle="Fleet · Dispatch · Trip Log · ALS/BLS Management"
      actions={<span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', padding: '6px 14px', background: '#fef2f2', borderRadius: 999, border: '1.5px solid #fca5a5' }}>Emergency: 108</span>}>
      {(fleetHook.error || tripsHook.error) && (
        <p className="hint" style={{ color: '#dc2626', marginBottom: 10 }}>
          {fleetHook.error || tripsHook.error}{' '}
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => { fleetHook.reload(); tripsHook.reload(); }}>Retry</button>
        </p>
      )}
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'dispatch', label: 'Dispatch' }, { id: 'fleet', label: 'Fleet Management' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'dispatch' && <DispatchTab trips={trips} ambulances={ambulances} loading={tripsHook.loading} onDispatch={onDispatch} onComplete={onComplete} />}
      {tab === 'fleet' && <FleetTab ambulances={ambulances} loading={fleetHook.loading} onSeedFleet={onSeedFleet} seeding={seeding} />}
    </PageShell>
  );
}
